/**
 * API client for OPA policy management
 */
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Update policy request structure
 */
export interface UpdatePolicyRequest {
  version: string;
  permissions: {
    aws?: {
      accounts: string[];
      regions: string[];
      allowed_iam_roles?: string[];
      partitions?: string[];
    };
    azure?: {
      subscriptions: string[];
      tenants: string[];
      regions: string[];
    };
    gcp?: {
      project_ids: string[];
      regions: string[];
    };
  };
}

/**
 * API response for successful update
 */
export interface UpdatePolicyResponse {
  success: boolean;
  message: string;
  repository: string;
  version: string;
}

/**
 * Client for interacting with OPA policy management API
 */
export class OpaPolicyClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  /**
   * Update a policy (PUT - full replacement)
   */
  async updatePolicy(
    repository: string,
    data: UpdatePolicyRequest,
  ): Promise<UpdatePolicyResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opa-policies');
    const url = `${baseUrl}/${encodeURIComponent(repository)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to update policy (${response.status}): ${errorText}`,
      );
    }

    return response.json();
  }

  /**
   * Patch a policy (PATCH - partial update)
   */
  async patchPolicy(
    repository: string,
    version: string,
    partialPermissions: Partial<UpdatePolicyRequest['permissions']>,
  ): Promise<UpdatePolicyResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('opa-policies');
    const url = `${baseUrl}/${encodeURIComponent(repository)}?version=${version}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(partialPermissions),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to patch policy (${response.status}): ${errorText}`,
      );
    }

    return response.json();
  }
}
